import { CircleLazyImage, ELoadingType, EMGirlType, Loading, MgirlNotice } from '@bilibili-maoer/prelude'
import { api, format } from '@bilibili-maoer/web-utils'

import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import { Stack, Tab, Tabs, styled } from '@mui/material'
import { mapValues } from 'lodash'
import moment from 'moment'
import useMount from 'react-use/esm/useMount'
import { match } from 'ts-pattern'

import { Button } from '~/core/components/button'
import { imageOptimizeRendering } from '~/core/components/image'
import { StyledRouterLink } from '~/core/components/link'
import { Title } from '~/core/components/title'
import { asMissEvanPagination } from '~/core/pagination'
import { initDateTimeRange } from '~/core/utils/date'
import { DateRange } from '~/features/guild/components/date-range'
import { GuildGuardWithRole } from '~/features/guild/components/router-guard'
import { FormattedTable } from '~/features/guild/components/table'
import { TableColumn } from '~/features/guild/components/table/types'
import { DataBlocksTitle, GuildTitle } from '~/features/guild/components/title'
import { DateRangeWrapper, Form } from '~/features/guild/components/wrappers'
import { GUILD_MIDDLE_TITLE } from '~/features/guild/constants'
import { GuildLiveIncomeItem, GuildLiveIncomeParams } from '~/features/guild/income/types'
import { EGuildIncomeStatus, EGuildIncomeType, ListTableRow } from '~/features/guild/types'
import { GuildApi } from '~/services/fm/guild'
import useAppNavigate from '~/shared/navigation/use-app-navigate'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'
import type { MilliSeconds } from '~/shared/types/base/units'
import { EGuildRole } from '~/shared/types/services/user'

type ReactLocationState = {
  iconurl: string
  username: string
  roomId: number
}

const CreatorInfoWrapper = styled('div')`
  display: flex;
  align-items: center;
  .avatar {
    ${imageOptimizeRendering}
    margin-right: 20px;
    border-radius: 50%;
  }
`

const CreatorInfoUsername = styled('p')`
  margin: 0;
  padding: 0;
  font-size: 25px;
`

const CreatorInfoCreatorId = styled('p')`
  margin: 0;
  padding: 0;
  margin-top: 4px;
  font-size: 15px;
`

const FormattedMgirlNotFound = styled(MgirlNotice)`
  padding-top: 100px;
  text-align: center;
  img {
    height: 170px;
  }
`
const INCOME_STATUS_MAP: any = {
  [EGuildIncomeStatus.Cancel]: '交易取消',
  [EGuildIncomeStatus.UnDone]: '交易未完成',
  [EGuildIncomeStatus.Done]: '交易成功',
}

const TAB_LIST: { [key in EGuildIncomeType]?: { name: string; title: string } } = {
  [EGuildIncomeType.Live]: {
    name: '礼物收益',
    title: '礼物总收益',
  },
  [EGuildIncomeType.Noble]: {
    name: '贵族开通收益',
    title: '贵族开通总收益',
  },
  [EGuildIncomeType.SuperFan]: {
    name: '超粉收益',
    title: '超粉总收益',
  },
  [EGuildIncomeType.Play]: {
    name: '玩法收益',
    title: '玩法总收益',
  },
}
const columns: TableColumn<ListTableRow<GuildLiveIncomeItem>>[] = [
  {
    field: 'confirm_time',
    headerTitle: '时间',
    renderBodyCell: (item) => format.formatUTCTimeBySeconds(item.row.confirm_time, 'YYYY-MM-DD HH:mm'),
  },
  {
    field: 'username',
    headerTitle: '发起方',
  },
  {
    field: 'title',
    headerTitle: '收益来源',
  },
  {
    field: 'revenue',
    headerTitle: '金额（元）',
    renderBodyCell: (item) => (item.row.revenue > 0 ? `+${item.row.revenue}` : item.row.revenue),
  },
  {
    field: 'status',
    headerTitle: '状态',
    renderBodyCell: (item) => INCOME_STATUS_MAP[item.row.status],
  },
]

export const GuildIncomeDetail = () => {
  const { live_id } = useParams()
  const [searchParams, setSearchParams] = useAppSearchParam()
  const location = useLocation() as { state?: ReactLocationState }
  const navigate = useAppNavigate()
  let liveIdNumber = parseInt(live_id || '0')
  if (Number.isNaN(liveIdNumber)) {
    liveIdNumber = 0
  }

  const time = initDateTimeRange({
    startTime: searchParams.get('start_date')
      ? (moment(searchParams.get('start_date')).valueOf() as MilliSeconds)
      : undefined,
    endTime: searchParams.get('end_date')
      ? (moment(searchParams.get('end_date')).valueOf() as MilliSeconds)
      : undefined,
  })
  const [timeRange, setTimeRange] = useState(time)
  const [total, setTotal] = useState(0)
  const [locationState] = useState(() => location.state)
  // 该页面只支持从一级页面进入（支持刷新），若通过链接直接访问会缺少数据，此时直接返回一级页面
  if (!locationState) {
    navigate('/guild/income')
  }

  const routeQuerySearchParams: GuildLiveIncomeParams = {
    creator_id: locationState?.roomId || 0,
    start_date: moment(time.startTime).format('YYYY-MM-DD'),
    end_date: moment(time.endTime).format('YYYY-MM-DD'),
    p: parseInt(searchParams.get('p') || '1'),
    type: parseInt(searchParams.get('type') || '0') as EGuildIncomeType,
  }

  const { data: incomeDetailData, isFetching } = GuildApi.getGuildLiveIncome.useQuery(routeQuerySearchParams)

  const pagination = incomeDetailData?.pagination && asMissEvanPagination(incomeDetailData.pagination)

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSearchParamsChange({
      start_date: moment(timeRange.startTime).format('YYYY-MM-DD'),
      end_date: moment(timeRange.endTime).format('YYYY-MM-DD'),
      p: 1,
    })
  }
  const handleSearchParamsChange = (newSearch: Partial<GuildLiveIncomeParams>) => {
    const newSearchParams = { ...routeQuerySearchParams, ...newSearch }
    setSearchParams(new URLSearchParams(mapValues(newSearchParams, (item) => item.toString())))
  }
  useEffect(() => {
    if (incomeDetailData?.total !== undefined) {
      setTotal(incomeDetailData.total)
    }
  }, [incomeDetailData])
  useMount(() => {
    handleSearchParamsChange(routeQuerySearchParams)
  })
  return (
    <GuildGuardWithRole requireOneOfRole={[EGuildRole.Owner, EGuildRole.Agent]}>
      <>
        <Title>{['公会收益', GUILD_MIDDLE_TITLE]}</Title>
        <Stack spacing={4}>
          <StyledRouterLink
            to={{
              pathname: `/guild/income`,
              search: new URLSearchParams({
                start_date: moment(time.startTime).format('YYYY-MM-DD'),
                end_date: moment(time.endTime).format('YYYY-MM-DD'),
              }).toString(),
            }}
            sx={{
              fontSize: '20px',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <NavigateBeforeIcon />
            返回
          </StyledRouterLink>
          <CreatorInfoWrapper>
            <a href={`${api.MISSEVAN_SITE_MAIN}/${live_id}`} target="_blank">
              <CircleLazyImage
                src={locationState?.iconurl || ''}
                alt={locationState?.username || ''}
                width={80}
                height={80}
                className="avatar"
              />
            </a>
            <div>
              <CreatorInfoUsername className="username">{locationState?.username || ''}</CreatorInfoUsername>
              <CreatorInfoCreatorId className="creator-id">ID: {liveIdNumber}</CreatorInfoCreatorId>
            </div>
          </CreatorInfoWrapper>
          <Tabs
            value={routeQuerySearchParams.type}
            onChange={(e, newValue) => handleSearchParamsChange({ type: newValue, p: 1 })}
            textColor="secondary"
            indicatorColor="secondary"
          >
            {Object.entries(TAB_LIST).map(([key, value]) => (
              <Tab key={key} value={parseInt(key)} label={value.name} disableRipple />
            ))}
          </Tabs>
          <Form onSubmit={handleFormSubmit}>
            <Stack spacing={4}>
              <DateRangeWrapper>
                <DateRange time={timeRange} onTimeRangeChange={setTimeRange} />
                <Button variant="contained" onClick={handleFormSubmit}>
                  查询
                </Button>
              </DateRangeWrapper>
              {match(isFetching)
                .with(true, () => <Loading style={{ margin: '140px auto 0' }} type={ELoadingType.Cat} />)
                .otherwise(() =>
                  incomeDetailData?.data ? (
                    <Stack spacing={2}>
                      <DataBlocksTitle>
                        <GuildTitle
                          title={`${moment(time.startTime).format('YYYY-MM-DD')} 至 ${moment(time.endTime).format(
                            'YYYY-MM-DD'
                          )} ${TAB_LIST[routeQuerySearchParams.type as EGuildIncomeType]?.title} ${total} 元`}
                        />
                      </DataBlocksTitle>
                      <FormattedTable
                        columns={columns}
                        rows={incomeDetailData.data}
                        page={routeQuerySearchParams.p}
                        count={pagination?.page_count || 1}
                        onChange={(_, page) => handleSearchParamsChange({ p: page })}
                      />
                    </Stack>
                  ) : (
                    <FormattedMgirlNotFound image={EMGirlType.NotFound} description="没有查询到数据哦~" />
                  )
                )}
            </Stack>
          </Form>
        </Stack>
      </>
    </GuildGuardWithRole>
  )
}
